import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { PLATFORM_META, KOL_SLOT_META, timeAgo } from "@/lib/meta";
import { Megaphone, Plus, Users, Image, MessageSquare, Repeat2, X, ChevronLeft, ShieldCheck, Link as LinkIcon, Download } from "lucide-react";

const TYPES = [
  { id: "kol", label: "KOL desk", icon: Users, desc: "Budget + mix → allocator fills → escrow" },
  { id: "burst", label: "Owned burst", icon: Megaphone, desc: "Extra posts on your proved channels" },
  { id: "meme", label: "Meme pack", icon: Image, desc: "Stills + captions (export soon)" },
  { id: "reddit", label: "Reddit", icon: MessageSquare, desc: "Drafts — connect Reddit to publish" },
  { id: "reply", label: "Reply raid", icon: Repeat2, desc: "Approve-before-send drafts" },
];
const WINDOWS = ["48h", "7d", "14d"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PROOF_HINT = { x: "x.com/…/status/…", telegram: "t.me/…", reddit: "reddit.com/…", tiktok: "tiktok.com/@…/video/…", instagram: "instagram.com/p/…", youtube: "youtu.be/… or youtube.com/watch" };
const Sim = () => <span className="rounded bg-ai/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide text-ai" data-testid="sim-badge">simulated</span>;

function SlotRow({ slot, onAction }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [payout, setPayout] = useState("");
  const [reason, setReason] = useState("");
  const [shot, setShot] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const meta = KOL_SLOT_META[slot.status] || { label: slot.status, cls: "bg-dust/10 text-dust border-line" };
  const plat = PLATFORM_META[slot.platform] || { label: slot.platform };
  const call = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const uploadShot = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/uploads/image", fd); setShot(data.path); toast.success("Screenshot attached"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  return (
    <div className="rounded-xl border border-line bg-raised/30 p-4" data-testid={`slot-${slot.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: plat.color }} />
          <span className="font-medium text-cream" data-testid={`slot-name-${slot.id}`}>{slot.kol_name}</span>
          <span className="font-mono text-xs text-dust">{plat.label} · {slot.handle}</span>
          <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide capitalize">{slot.tier}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-cream">${slot.price_usd}</span>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.cls}`} data-testid={`slot-status-${slot.id}`}>{meta.label}</span>
        </div>
      </div>

      {slot.principal != null && (
        <p className="mt-1 font-mono text-[11px] text-dust/80">escrow ${slot.principal} · cut ${slot.protocol_cut} → Fee Router {slot.simulated && <Sim />}</p>
      )}
      {slot.proof_url && <p className="mt-1 truncate font-mono text-[11px] text-ai">proof: {slot.proof_url}</p>}
      {slot.proof_screenshot && <img src={`${BACKEND}/api/files/${slot.proof_screenshot}`} alt="proof" className="mt-1 h-16 rounded border border-line object-cover" data-testid={`slot-shot-${slot.id}`} />}
      {slot.auto_verified === false && slot.status === "proof_submitted" && <p className="mt-1 text-[11px] text-warn">auto-verify inconclusive — needs manual approval</p>}
      {slot.auto_verified === true && <p className="mt-1 text-[11px] text-pulp">auto-verified ✓</p>}
      {slot.payout_tx && <p className="mt-1 font-mono text-[11px] text-pulp">paid {slot.payout_tx.slice(0, 12)}… <Sim /></p>}
      {slot.magic_token && ["offered", "in_escrow"].includes(slot.status) && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/kol/${slot.magic_token}`); toast.success("KOL link copied"); }}
            className="flex items-center gap-1 text-[11px] text-ai hover:underline" data-testid={`slot-copy-link-${slot.id}`}>
            <LinkIcon size={12} /> Copy KOL magic link
          </button>
          {slot.link_sent_at && <span className="text-[11px] text-dust">link sent {timeAgo(slot.link_sent_at)} <Sim /></span>}
          <button onClick={() => call(() => onAction(slot.id, "resend-link"))} disabled={busy} className="text-[11px] text-dust hover:text-cream" data-testid={`slot-resend-${slot.id}`}>Resend</button>
        </div>
      )}

      {/* Demo KOL actions */}
      <div className="mt-3 border-t border-line/60 pt-3">
        <p className="mb-2 text-[10px] uppercase tracking-wider text-dust/70">Demo KOL actions</p>
        {slot.status === "offered" && (
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={() => call(() => onAction(slot.id, "accept"))} className="h-8 bg-pulp text-ink hover:bg-pulp/90" data-testid={`slot-accept-${slot.id}`}>Accept</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => call(() => onAction(slot.id, "decline"))} className="h-8" data-testid={`slot-decline-${slot.id}`}>Decline</Button>
          </div>
        )}
        {slot.status === "in_escrow" && (
          <div className="space-y-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={`Proof URL (${PROOF_HINT[slot.platform] || "public link"})`} className="h-8 bg-ink text-xs" data-testid={`slot-proof-url-${slot.id}`} />
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (must mention ticker or CA)" className="h-8 bg-ink text-xs" data-testid={`slot-proof-caption-${slot.id}`} />
            <div className="flex items-center gap-2">
              <label className="cursor-pointer rounded-lg border border-line bg-ink px-2 py-1.5 text-[11px] text-dust hover:text-cream" data-testid={`slot-shot-label-${slot.id}`}>
                {uploading ? "Uploading…" : shot ? "Screenshot ✓" : "+ Screenshot"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadShot(e.target.files?.[0])} data-testid={`slot-shot-input-${slot.id}`} />
              </label>
              <Button size="sm" disabled={busy || !url} onClick={() => call(() => onAction(slot.id, "proof", { url, caption, screenshot_path: shot }))} className="h-8 bg-ai text-ink hover:bg-ai/90" data-testid={`slot-submit-proof-${slot.id}`}>Submit proof</Button>
            </div>
          </div>
        )}
        {slot.status === "proof_submitted" && (
          <div className="space-y-2">
            {!(slot.payout_wallet && slot.payout_verified) ? (
              <div className="flex gap-2">
                <Input value={payout} onChange={(e) => setPayout(e.target.value)} placeholder="KOL payout wallet" className="h-8 bg-ink text-xs" data-testid={`slot-payout-${slot.id}`} />
                <Button size="sm" disabled={busy || payout.length < 6} onClick={() => call(() => onAction(slot.id, "bind-payout", { wallet: payout }))} className="h-8" data-testid={`slot-bind-${slot.id}`}>Bind</Button>
              </div>
            ) : (
              <p className="font-mono text-[11px] text-pulp">payout bound {slot.payout_wallet.slice(0, 8)}… ✓</p>
            )}
            <div className="flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => call(() => onAction(slot.id, "approve"))} className="h-8 bg-pulp text-ink hover:bg-pulp/90" data-testid={`slot-approve-${slot.id}`}>Approve &amp; release</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => call(() => onAction(slot.id, "dispute", { reason: "content off-brief" }))} className="h-8" data-testid={`slot-dispute-${slot.id}`}>Dispute</Button>
            </div>
          </div>
        )}
        {slot.status === "disputed" && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={busy} onClick={() => call(() => onAction(slot.id, "resolve", { outcome: "release" }))} className="h-8 bg-pulp text-ink" data-testid={`slot-resolve-release-${slot.id}`}>Release</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => call(() => onAction(slot.id, "resolve", { outcome: "refund" }))} className="h-8" data-testid={`slot-resolve-refund-${slot.id}`}>Refund</Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => call(() => onAction(slot.id, "resolve", { outcome: "slash" }))} className="h-8 text-danger" data-testid={`slot-resolve-slash-${slot.id}`}>Slash → burn</Button>
          </div>
        )}
        {["released", "refunded", "expired", "declined", "slashed_burn"].includes(slot.status) && (
          <p className="text-[11px] text-dust">Closed — {meta.label.toLowerCase()}.</p>
        )}
      </div>
    </div>
  );
}

function KolCampaignView({ campaignId, onBack }) {
  const [c, setC] = useState(null);
  const load = useCallback(() => api.get(`/campaigns/${campaignId}`).then(({ data }) => setC(data)).catch(() => {}), [campaignId]);
  useEffect(() => { load(); }, [load]);
  const doAction = async (slotId, action, body) => {
    try {
      const { data } = await api.post(`/slots/${slotId}/${action}`, body || {});
      if (action === "resend-link") toast.success("Magic link re-sent (SIMULATED)");
      else toast.success(`${data.kol_name}: ${(KOL_SLOT_META[data.status] || {}).label || data.status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  if (!c) return <p className="text-sm text-dust">Loading campaign…</p>;
  return (
    <div className="space-y-4" data-testid="kol-campaign-view">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-dust hover:text-cream" data-testid="kol-back"><ChevronLeft size={14} /> Campaigns</button>
      <div className="rounded-2xl border border-line bg-surface p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold"><Users size={15} className="text-pulp" /> KOL desk {c.simulated && <Sim />}</h3>
          <span className="font-mono text-sm text-cream" data-testid="kol-budget">${c.budget_usdc} budget</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-3 text-xs text-dust">
          <span>Mix: {Object.entries(c.mix || {}).map(([k, v]) => `${PLATFORM_META[k]?.label || k} ${v}%`).join(" · ")}</span>
          <span>Window {c.window}</span>
          <span data-testid="kol-dust">Dust ${c.dust_usdc}</span>
          <span>Reallocate {c.allow_reallocation ? "on" : "off"}</span>
        </div>
        <p className="mt-2 flex items-center gap-1 text-[11px] text-dust/70"><ShieldCheck size={12} className="text-pulp" /> Accept locks escrow (−15% protocol cut). Release needs approved proof + bound payout. No-show → refund or $MOON burn.</p>
      </div>
      <div className="space-y-3">
        {(c.slots || []).map((s) => <SlotRow key={s.id} slot={s} onAction={doAction} />)}
        {!(c.slots || []).length && <p className="text-sm text-dust">No slots — budget too small to fill any KOL.</p>}
      </div>
    </div>
  );
}

function NewCampaign({ launchId, launch, platforms, onClose, onDone }) {
  const [type, setType] = useState("kol");
  const [budget, setBudget] = useState(500);
  const [mix, setMix] = useState({ x: 80, telegram: 20 });
  const [win, setWin] = useState("7d");
  const [realloc, setRealloc] = useState(false);
  const [approve, setApprove] = useState(true);
  const [autoRelease, setAutoRelease] = useState(false);
  const [posts, setPosts] = useState(6);
  const [brief, setBrief] = useState("");
  const [tone, setTone] = useState("degen");
  const [busy, setBusy] = useState(false);
  const enabled = Object.entries(platforms || {}).filter(([, v]) => v.enabled).map(([k]) => k);
  const mixSum = Object.values(mix).reduce((a, b) => a + (Number(b) || 0), 0);
  const setMixVal = (k, v) => setMix((m) => ({ ...m, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      let body = { type };
      if (type === "kol") body = { type, budget_usdc: Number(budget), mix: Object.fromEntries(Object.entries(mix).filter(([, v]) => v > 0)), window: win, allow_reallocation: realloc, approve_proof: approve, auto_release: autoRelease };
      else if (type === "burst") body = { type, posts: Number(posts) };
      else if (type === "reddit") body = { type, subreddit: brief };
      else if (type === "meme") body = { type, tone };
      else body = { type, brief };
      const { data } = await api.post(`/launches/${launchId}/campaigns`, body);
      toast.success(type === "kol" ? `Campaign live — ${(data.slots || []).length} offers out` : "Campaign created");
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create campaign."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4" data-testid="new-campaign-modal">
      <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-line bg-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">New campaign</h3>
          <button onClick={onClose} className="text-dust hover:text-cream" data-testid="new-campaign-close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {TYPES.map((t) => (
            <button key={t.id} onClick={() => setType(t.id)} data-testid={`ctype-${t.id}`}
              className={`rounded-xl border p-3 text-left transition-colors ${type === t.id ? "border-juice bg-juice/10" : "border-line bg-raised/30 hover:bg-raised"}`}>
              <t.icon size={16} className="mb-1 text-pulp" />
              <p className="text-xs font-medium text-cream">{t.label}</p>
              <p className="text-[10px] leading-tight text-dust">{t.desc}</p>
            </button>
          ))}
        </div>

        {type === "kol" && (
          <div className="mt-5 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-dust">Budget (USDC)</label>
              <Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} className="mt-1 bg-ink" data-testid="kol-budget-input" />
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs uppercase tracking-wider text-dust">Platform mix</label>
                <span className={`font-mono text-xs ${mixSum === 100 ? "text-pulp" : "text-danger"}`} data-testid="mix-sum">{mixSum}%</span>
              </div>
              <div className="space-y-3">
                {["x", "telegram", "reddit", "tiktok", "instagram", "youtube"].map((p) => {
                  const en = platforms?.[p]?.enabled;
                  return (
                    <div key={p} className={`flex items-center gap-3 ${en ? "" : "opacity-40"}`}>
                      <span className="w-20 text-xs" style={{ color: PLATFORM_META[p]?.color }}>{PLATFORM_META[p]?.label}</span>
                      <Slider disabled={!en} min={0} max={100} step={5} value={[mix[p] || 0]} onValueChange={(v) => setMixVal(p, v[0])} className="flex-1" data-testid={`mix-slider-${p}`} />
                      <span className="w-10 text-right font-mono text-xs text-dust" data-testid={`mix-val-${p}`}>{mix[p] || 0}%</span>
                      {!en && <span className="text-[9px] text-dust/60">no roster</span>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {WINDOWS.map((w) => (
                <button key={w} onClick={() => setWin(w)} data-testid={`win-${w}`}
                  className={`rounded-lg border px-3 py-1.5 text-xs ${win === w ? "border-juice bg-juice/10 text-cream" : "border-line text-dust"}`}>{w}</button>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-xl border border-line bg-raised/30 p-3">
              <div><p className="text-sm text-cream">Allow reallocation</p><p className="text-[11px] text-dust">Move leftover under a slot price to another enabled platform</p></div>
              <Switch checked={realloc} onCheckedChange={setRealloc} data-testid="toggle-realloc" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-line bg-raised/30 p-3">
              <div><p className="text-sm text-cream">Approve proof before release</p><p className="text-[11px] text-dust">Recommended — no auto-release</p></div>
              <Switch checked={approve} onCheckedChange={setApprove} data-testid="toggle-approve" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-line bg-raised/30 p-3">
              <div><p className="text-sm text-cream">Auto-release on verified proof</p><p className="text-[11px] text-dust">Best-effort: proof URL must load and mention the ticker/CA, then pay automatically</p></div>
              <Switch checked={autoRelease} onCheckedChange={setAutoRelease} data-testid="toggle-auto-release" />
            </div>
            <Button disabled={busy || mixSum !== 100 || Number(budget) < 100} onClick={submit} className="w-full bg-pulp text-ink hover:bg-pulp/90" data-testid="kol-submit">
              {busy ? "Allocating…" : `Start KOL campaign — $${budget}`}
            </Button>
            {mixSum !== 100 && <p className="text-center text-[11px] text-danger">Mix must sum to 100%.</p>}
          </div>
        )}

        {type === "burst" && (
          <div className="mt-5 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-dust">Extra posts</label>
              <Input type="number" value={posts} onChange={(e) => setPosts(e.target.value)} className="mt-1 bg-ink" data-testid="burst-posts" />
              <p className="mt-1 text-[11px] text-dust/70">Queued on your proved channels. Missing creds → drafts stay queued.</p>
            </div>
            <Button disabled={busy} onClick={submit} className="w-full bg-pulp text-ink hover:bg-pulp/90" data-testid="burst-submit">{busy ? "Queuing…" : "Queue burst"}</Button>
          </div>
        )}

        {type === "meme" && (
          <div className="mt-5 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-dust">Caption tone</label>
              <div className="mt-1 flex gap-2">
                {["degen", "clean", "hype"].map((t) => (
                  <button key={t} onClick={() => setTone(t)} data-testid={`meme-tone-${t}`}
                    className={`rounded-lg border px-3 py-1.5 text-xs capitalize ${tone === t ? "border-juice bg-juice/10 text-cream" : "border-line text-dust"}`}>{t}</button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-dust/70">4 stills (9:16 + 1:1) generated from your logo with {tone} captions.</p>
            <Button disabled={busy} onClick={submit} className="w-full bg-raised text-cream hover:bg-line/40" data-testid="draft-submit">{busy ? "Working…" : "Generate meme pack"}</Button>
          </div>
        )}

        {["reddit", "reply"].includes(type) && (
          <div className="mt-5 space-y-4">
            {type === "reddit" && (
              <>
                <Input value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Subreddit (e.g. CryptoMoonShots)" className="bg-ink" data-testid="reddit-subreddit" />
                <p className="text-[11px] text-dust/70">Leave subreddit empty to just draft. With Reddit connected + a subreddit, this posts live.</p>
              </>
            )}
            {type !== "reddit" && <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} placeholder="Brief (optional)" className="bg-ink" data-testid="draft-brief" />}
            <p className="text-[11px] text-dust/70">{type === "reddit" ? "Title + body auto-drafted from your logo/brief." : "Drafts generate now; publishing ships in a later slice."}</p>
            <Button disabled={busy} onClick={submit} className="w-full bg-raised text-cream hover:bg-line/40" data-testid="draft-submit">{busy ? "Working…" : (type === "reddit" ? "Draft / post to Reddit" : "Create draft campaign")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CampaignsPanel({ launchId, launch }) {
  const [campaigns, setCampaigns] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    api.get(`/launches/${launchId}/campaigns`).then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {});
    api.get(`/kol/platforms`).then(({ data }) => setPlatforms(data.platforms || {})).catch(() => {});
  }, [launchId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!campaigns.some((c) => c.type === "meme" && c.meme_status === "generating")) return;
    const t = setInterval(() => api.get(`/launches/${launchId}/campaigns`).then(({ data }) => setCampaigns(data.campaigns || [])).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [campaigns, launchId]);

  const downloadZip = async (cid) => {
    try {
      const res = await api.get(`/campaigns/${cid}/meme.zip`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = u; a.download = "meme_pack.zip"; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Zip not ready yet."); }
  };

  if (openId) return <KolCampaignView campaignId={openId} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div className="space-y-4" data-testid="campaigns-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-cream">Campaigns</h2>
          <p className="text-xs text-dust">Keep growing after mint — desks run always-on; campaigns are à-la-carte.</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="gap-1 bg-pulp text-ink hover:bg-pulp/90" data-testid="new-campaign-btn"><Plus size={15} /> New campaign</Button>
      </div>

      {!campaigns.length && <div className="rounded-2xl border border-dashed border-line bg-raised/20 p-8 text-center text-sm text-dust" data-testid="campaigns-empty">No campaigns yet. Spin up a KOL desk or an owned-channel burst.</div>}

      <div className="grid gap-3 sm:grid-cols-2">
        {campaigns.map((c) => (
          <div key={c.id} onClick={() => c.type === "kol" && setOpenId(c.id)} data-testid={`campaign-card-${c.id}`}
            className={`rounded-2xl border border-line bg-surface p-4 text-left transition-colors ${c.type === "kol" ? "cursor-pointer hover:border-pulp/40" : ""}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-cream">{c.title || c.type}</span>
              <span className="rounded-full border border-line px-2 py-0.5 text-[10px] uppercase tracking-wide text-dust">{c.status}</span>
            </div>
            {c.type === "kol" ? (
              <p className="mt-1 font-mono text-xs text-dust">${c.budget_usdc} · {c.slot_count} slot(s) · {Object.entries(c.mix || {}).map(([k, v]) => `${k} ${v}%`).join(" ")}</p>
            ) : c.type === "meme" ? (
              <div className="mt-1">
                <p className="text-xs text-dust">{c.note || (c.meme_status === "generating" ? "Generating stills…" : "")}</p>
                {c.meme_status === "ready" && (c.meme_assets || []).length > 0 && (
                  <>
                    <div className="mt-2 flex gap-1.5">
                      {(c.meme_assets || []).slice(0, 4).map((a, i) => (
                        <img key={i} src={`${BACKEND}/api/files/${a.path}`} alt="still" className="h-14 w-9 rounded border border-line object-cover" data-testid={`meme-thumb-${c.id}-${i}`} />
                      ))}
                    </div>
                    <Button size="sm" onClick={() => downloadZip(c.id)} className="mt-2 h-8 gap-1 bg-pulp text-ink hover:bg-pulp/90" data-testid={`meme-download-${c.id}`}><Download size={13} /> Download zip</Button>
                  </>
                )}
              </div>
            ) : (
              <p className="mt-1 text-xs text-dust">{c.note || (c.posts ? `${c.posts} posts on ${(c.channels || []).join(", ")}` : "")}</p>
            )}
            <p className="mt-2 text-[10px] text-dust/60">{timeAgo(c.created_at)}{c.type === "kol" && " · open board →"}</p>
          </div>
        ))}
      </div>

      {showNew && <NewCampaign launchId={launchId} launch={launch} platforms={platforms} onClose={() => setShowNew(false)} onDone={(d) => { setShowNew(false); load(); if (d?.type === "kol") setOpenId(d.id); }} />}
    </div>
  );
}
